import { useCallback } from 'react';
import { getCookie } from '../../../functions/getCookie';
import i18n from '../../../i18n';
import useForm from '../../../components/Form/use-form';

/**
 * Get standard cart headers for API requests
 */
export const getCartHeaders = () => {
    return {
        'X-CSRFToken': getCookie('csrftoken'),
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
    };
};

export const resolveOptionProductId = (option) => {
    const nested = option?.product_object;
    if (nested && typeof nested === 'object' && nested.id) return nested.id;
    const product = option?.product;
    if (product && typeof product === 'object' && product.id) return product.id;
    return product || null;
};

export const resolveOptionLineQuantity = (option, selectedOptions = {}) => {
    const configuredQty = Number(option?.quantity);
    const fallbackQty = Number.isFinite(configuredQty) && configuredQty > 0 ? configuredQty : 1;
    const optionValue = selectedOptions?.[option?.id] ?? selectedOptions?.[String(option?.id)];
    const numericValue = typeof optionValue === 'number' ? optionValue : Number(optionValue);

    if (option?.is_required) {
        return fallbackQty;
    }
    if (option?.selection_type === 'yes_no') {
        const selected = optionValue === true || (Number.isFinite(numericValue) && numericValue > 0);
        return selected ? fallbackQty : 0;
    }
    if (Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
    }
    return 0;
};

export const resolveOptionUnitPrice = (option) => {
    const nested = option?.product_object;
    const raw = option?.price_after_discount ?? option?.price
        ?? nested?.price_after_discount ?? nested?.price
        ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
};

export const useCartActions = () => {
    const formHandler = () => {};
    const { isLoading: formLoading, error: formError, sendRequest: submitFormData } = useForm(formHandler);

    const addProductToCart = useCallback(async ({
        product,
        quantity = 1,
        variantResult = null,
        selectedOptions = {},
        compositionSelections = null,
        hasVariants = false,
        mainProductId = null,
    }) => {
        const productId = mainProductId || product?.id;

        if (hasVariants && !variantResult) {
            throw new Error(i18n.t('Please select a variant'));
        }

        if (!productId) {
            throw new Error(i18n.t('Unable to add product to cart'));
        }

        const childLines = [];
        if (product?.product_options) {
            product.product_options.forEach((option) => {
                const optionProductId = resolveOptionProductId(option);
                if (!optionProductId) return;

                const lineQuantity = resolveOptionLineQuantity(option, selectedOptions);
                if (lineQuantity > 0) {
                    childLines.push({
                        product: optionProductId,
                        quantity: lineQuantity,
                        line_role: 'option',
                        child_lines: [],
                    });
                }
            });
        }

        const cartLineData = {
            product: productId,
            quantity,
            child_lines: childLines,
        };
        if (compositionSelections && Object.keys(compositionSelections).length > 0) {
            cartLineData.composition_selections = compositionSelections;
        }

        return submitFormData({
            url: '/rest/cart/add_cart_store/',
            method: 'POST',
            // Do not send `line`. Send `cart_lines` with nested `cart_lines`.
            data: JSON.stringify({ cart_lines: [cartLineData] }),
            headers: getCartHeaders(),
        });
    }, [submitFormData]);

    return {
        isLoading: formLoading,
        error: formError,
        addProductToCart,
    };
};
